import React, {useState} from 'react';
import {Pressable, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {Icon} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {controls, radii, shadows, spacing, useTheme} from '@/shared/ui/theme';

/**
 * A section that is closed until it is wanted, and says what is inside while closed.
 *
 * The report screens used to hide their two heaviest blocks — every department, every
 * employee — behind exclusive tabs. A tab is the wrong instrument for that: it makes
 * the reader choose a VIEW before they have a question, it hides that the other two
 * exist while you are in one of them, and it throws away the reading order the page
 * should have had. A page of sections keeps the order and keeps everything reachable
 * by scrolling.
 *
 * Two rules make that work rather than turning the page into an accordion maze:
 *
 *   - A CLOSED SECTION STATES ITS CONTENT. «По сотрудникам · 43 человека» is already
 *     an answer; opening it is for when the answer is not enough. A row that says only
 *     «По сотрудникам» is a door with no sign on it.
 *   - OPENING IS WHAT FETCHES. The department comparison is N requests, one per
 *     department. Behind a tab that cost was paid on a tap nobody thought of as a
 *     query; here the screen holds the open state, so it decides when to ask.
 */
const styles = StyleSheet.create({
  root: {borderRadius: radii.xl, borderWidth: StyleSheet.hairlineWidth, overflow: 'hidden'},
  head: {flexDirection: 'row', alignItems: 'center'},
  /* The toggle proper: the title, its summary, and the room to the right of them. */
  toggle: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingLeft: spacing(4),
    paddingVertical: spacing(4),
  },
  /* The aside and the chevron pay their own right-hand padding, so the row's tap
     targets meet instead of leaving a dead strip between them. */
  aside: {paddingHorizontal: spacing(2)},
  chevronTap: {
    paddingRight: spacing(4),
    paddingLeft: spacing(2),
    paddingVertical: spacing(4),
    /* 16 + 8 + 18pt глифа = 42, на два меньше пола касания. В вебе `hitSlop` не
       работает — коробка и есть цель, поэтому недостающие два она берёт себе. */
    minWidth: controls.lg,
    alignItems: 'center',
  },
  text: {flex: 1, gap: 1},
  chevron: {transform: [{rotate: '90deg'}]},
  chevronOpen: {transform: [{rotate: '270deg'}]},
  body: {
    paddingHorizontal: spacing(4),
    paddingBottom: spacing(4),
    gap: spacing(3),
  },
  rule: {height: StyleSheet.hairlineWidth, marginBottom: spacing(4)},
});

export function Disclosure({
  title,
  /** What is inside, in words — read instead of opening, most of the time. */
  summary,
  open,
  onToggle,
  /** An `Explain` mark or similar, on the heading's own line. */
  aside,
  children,
  style,
  testID,
}: {
  title: string;
  summary?: string;
  open: boolean;
  onToggle: () => void;
  aside?: React.ReactNode;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <View
      testID={testID}
      style={[
        styles.root,
        {backgroundColor: p.card, borderColor: p.border},
        shadows.panel,
        style,
      ]}>
      {/*
        The header is a ROW of two pressables with the aside between them, not one
        pressable containing everything — because `aside` is an `Explain` mark, which is
        itself a Pressable. On web react-native-web renders both as `<button>`, and a
        button inside a button is invalid HTML: React 19 warns on every render, the inner
        control's clicks are undefined behaviour, and a screen reader is handed a control
        nested inside another control.

        Splitting it also keeps the reading order the row wants — title … ⓘ … ⌄ — which
        putting the aside after a single pressable would have reversed.

        Hover is lifted here so the whole row still lights as one object; the second
        pressable carries no `accessibilityRole`, so it stays a plain div, and it is
        hidden from assistive tech because it does exactly what the first one already
        announces.
      */}
      <View style={[styles.head, hovered && {backgroundColor: p.accent}]}>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{expanded: open}}
          onPress={onToggle}
          onHoverIn={() => setHovered(true)}
          onHoverOut={() => setHovered(false)}
          testID={testID ? `${testID}-toggle` : undefined}
          style={styles.toggle}>
          <View style={styles.text}>
            <AppText variant="heading">{title}</AppText>
            {summary ? <AppText variant="caption">{summary}</AppText> : null}
          </View>
        </Pressable>

        {aside ? <View style={styles.aside}>{aside}</View> : null}

        <Pressable
          onPress={onToggle}
          onHoverIn={() => setHovered(true)}
          onHoverOut={() => setHovered(false)}
          accessibilityElementsHidden
          importantForAccessibility="no"
          style={styles.chevronTap}>
          <View style={open ? styles.chevronOpen : styles.chevron}>
            <Icon name="chevron" size={18} color={p.mutedForeground} />
          </View>
        </Pressable>
      </View>

      {open ? (
        <View style={styles.body}>
          <View style={[styles.rule, {backgroundColor: p.border}]} />
          {children}
        </View>
      ) : null}
    </View>
  );
}
